import App from '@/App';
import { AuthLayout, EmptyLayout, ErrorBoundary, InternalsLayout, MainLayout } from '@/components';
import {
    AddBroadcastPage,
    AddEmailPage,
    AddFlowPage,
    AffiliateAnalyticsPage,
    AffiliateLinksPage,
    AnalyticsPage,
    AppointmentDetailsPage,
    AppointmentsPage,
    AuthPage,
    CourseAnalyticsPage,
    CustomerDetailsPage,
    CustomersPage,
    DashboardPage,
    EditBroadcastPage,
    EditEmailPage,
    EmailFlowsPage,
    EmailRecipientsPage,
    ForgotPasswordPage,
    IncomePage,
    IntegrationAuthorizationPage,
    InternalsCalendarPage,
    InternalsChartsPage,
    InternalsComponentsPage,
    InternalsDataRowPage,
    InternalsFileUploadPage,
    InternalsNavigationConfirmationPage,
    InternalsPage,
    InternalsSegmentedControlPage,
    InternalsTabsPage,
    InternalsTipTapEditorPage,
    LoginPage,
    MoreOptionsPage,
    PaymentDetailsPage,
    ProfilePage,
    ReferralsPage,
    ResetPasswordPage,
    SettingsPage,
    StoresPage,
    SupportPage,
} from '@/pages';
import AddProductPage from '@/pages/AddProductPage';
import AutomationsPage from '@/pages/AutomationsPage';
import EditProductPage from '@/pages/EditProductPage';
import EditorPage from '@/pages/EditorPage';
import RegisterPage from '@/pages/RegisterPage';
import CompleteAffiliateRegistrationPage from '@/pages/auth/CompleteAffiliateRegistrationPage';
import SubscribePaymentPage from '@/pages/auth/SubscribePaymentPage';
import SubscribePlanPage from '@/pages/auth/SubscribePlanPage';
import SubscribeSocialsPage from '@/pages/auth/SubscribeSocialsPage';
import {
    AdsTrackingProvider,
    AuthProvider,
    ClarityProvider,
    GoogleAnalyticsProvider,
    MiscProvider,
    StoreProvider,
    StripeProvider,
    UserflowProvider,
    UserProvider,
} from '@/providers';
import { queryClient } from '@/queries';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate } from 'react-router-dom';

export const router = createBrowserRouter([
    {
        // ErrorBoundary catches hook ordering errors that can occur during logout
        // when components re-render while authentication state is changing
        errorElement: <ErrorBoundary />,
        element: (
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <UserflowProvider>
                        <ClarityProvider>
                            <GoogleAnalyticsProvider>
                                <AdsTrackingProvider>
                                    <UserProvider>
                                        <MiscProvider>
                                            <StripeProvider>
                                                <StoreProvider>
                                                    <App />
                                                </StoreProvider>
                                            </StripeProvider>
                                        </MiscProvider>
                                    </UserProvider>
                                </AdsTrackingProvider>
                            </GoogleAnalyticsProvider>
                        </ClarityProvider>
                    </UserflowProvider>
                </AuthProvider>
            </QueryClientProvider>
        ),
        children: [
            // Auth Layout Routes (no sidebar/navbar)
            {
                element: <AuthLayout />,
                children: [
                    {
                        path: '/auth',
                        element: <AuthPage />,
                    },
                    {
                        path: '/register',
                        element: <RegisterPage />,
                    },
                    {
                        path: '/login',
                        element: <LoginPage />,
                    },
                    {
                        path: '/forgot_password',
                        element: <ForgotPasswordPage />,
                    },
                    {
                        path: '/reset-password',
                        element: <ResetPasswordPage />,
                    },
                    {
                        path: '/subscribe/socials',
                        element: <SubscribeSocialsPage />,
                    },
                    {
                        path: '/subscribe/plan',
                        element: <SubscribePlanPage />,
                    },
                    {
                        path: '/subscribe/start',
                        element: <SubscribePaymentPage />,
                    },
                    {
                        path: '/complete-registration',
                        element: <CompleteAffiliateRegistrationPage />,
                    },
                ],
            },
            {
                path: '/editor',
                element: <EditorPage />,
            },
            // Main Layout Routes (with sidebar/navbar)
            {
                element: <MainLayout />,
                children: [
                    {
                        path: '/dashboard',
                        element: <DashboardPage />,
                    },
                    {
                        path: '/profile',
                        element: <ProfilePage />,
                    },
                    {
                        path: '/account_settings',
                        element: <SettingsPage />,
                    },
                    {
                        path: '/stores',
                        children: [
                            {
                                index: true,
                                element: <StoresPage />,
                            },
                            {
                                path: ':storeId/page/create',
                                element: <AddProductPage />,
                            },
                            {
                                path: ':storeId/page/:pageType/create',
                                element: <EditProductPage />,
                            },
                            {
                                path: ':storeId/page/:pageType/:pageId',
                                element: <EditProductPage />,
                            },
                        ],
                    },
                    {
                        path: '/flows',
                        children: [
                            {
                                index: true,
                                element: <EmailFlowsPage />,
                            },
                            {
                                path: 'new',
                                element: <AddFlowPage />,
                            },
                            {
                                path: ':flowId/new',
                                element: <AddEmailPage />,
                            },
                            {
                                path: ':flowId/emails/:emailId',
                                element: <EditEmailPage />,
                            },
                            {
                                path: '-1/broadcast/new',
                                element: <AddBroadcastPage />,
                            },
                            {
                                path: ':flowId/broadcast/emails/:emailId',
                                element: <EditBroadcastPage />,
                            },
                            {
                                path: 'recipients/:emailId',
                                element: <EmailRecipientsPage />,
                            },
                            {
                                path: 'recipients/:emailId/:page?',
                                element: <EmailRecipientsPage />,
                            },
                        ],
                    },
                    {
                        path: '/automations',
                        element: <AutomationsPage />,
                    },
                    {
                        path: '/income',
                        children: [
                            {
                                index: true,
                                element: <IncomePage />,
                            },
                            {
                                path: 'details/:paymentId',
                                element: <PaymentDetailsPage />,
                            },
                        ],
                    },
                    {
                        path: '/audience',
                        element: <CustomersPage />,
                    },
                    {
                        path: '/audience/details/:userFanId',
                        element: <CustomerDetailsPage />,
                    },
                    {
                        path: '/analytics',
                        children: [
                            {
                                index: true,
                                element: <AnalyticsPage />,
                            },
                            {
                                path: 'affiliate/:pageId',
                                element: <AffiliateAnalyticsPage />,
                            },
                        ],
                    },
                    {
                        path: '/analytics/course/:courseId',
                        element: <CourseAnalyticsPage />,
                    },
                    {
                        path: '/affiliate',
                        element: <AffiliateLinksPage />,
                    },
                    {
                        path: '/appointments/:view?',
                        element: <AppointmentsPage />,
                    },
                    {
                        path: '/appointments/details/:id',
                        element: <AppointmentDetailsPage />,
                    },
                    {
                        path: '/referrals',
                        element: <ReferralsPage />,
                    },
                    {
                        path: '/apps',
                        element: <MoreOptionsPage />,
                    },
                    // Internals nested layout
                    {
                        path: '/internals',
                        element: <InternalsLayout />,
                        children: [
                            {
                                index: true,
                                element: <InternalsPage />,
                            },
                            {
                                path: 'charts',
                                element: <InternalsChartsPage />,
                            },
                            {
                                path: 'calendar',
                                element: <InternalsCalendarPage />,
                            },
                            {
                                path: 'components',
                                element: <InternalsComponentsPage />,
                            },
                            {
                                path: 'data-row',
                                element: <InternalsDataRowPage />,
                            },
                            {
                                path: 'tiptap-editor',
                                element: <InternalsTipTapEditorPage />,
                            },
                            {
                                path: 'segmented-control',
                                element: <InternalsSegmentedControlPage />,
                            },
                            {
                                path: 'tabs',
                                element: <InternalsTabsPage />,
                            },
                            {
                                path: 'navigation-confirmation',
                                element: <InternalsNavigationConfirmationPage />,
                            },
                            {
                                path: 'unsplash-image-selector',
                                element: <InternalsFileUploadPage />,
                            },
                        ],
                    },
                ],
            },
            {
                path: '/support',
                element: <EmptyLayout />,
                children: [
                    {
                        index: true,
                        element: <SupportPage />,
                    },
                ],
            },
            // Integration Authorization (no layout)
            {
                path: '/integration-authorization',
                element: <IntegrationAuthorizationPage />,
            },

            // Catch-all redirect
            {
                path: '*',
                element: <Navigate to='/dashboard' />,
            },
        ],
    },
]);
